import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Chip, CircularProgress, Alert, Snackbar,
  TextField, Stack, Card, CardContent, Grid, Tooltip, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LockIcon from '@mui/icons-material/Lock';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PeopleIcon from '@mui/icons-material/People';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import EditIcon from '@mui/icons-material/Edit';
import UndoIcon from '@mui/icons-material/Undo';
import HistoryIcon from '@mui/icons-material/History';
import RestoreIcon from '@mui/icons-material/Restore';
import {
  adminGetAllCommissions,
  adminSnapshotUser,
  adminSnapshotAll,
  adminGetCommissionAdjustments,
  adminAdjustCommission,
  adminRevertCommissionAdjustment,
  adminRollbackCommissionAdjustment
} from '../../services/commissionService';

const formatMoney = (amount) => {
  if (!amount && amount !== 0) return '0 ₫';
  return new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND', maximumFractionDigits: 0 }).format(amount);
};

const CommissionManagement = ({ isMobile = false }) => {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

  const [monthKey, setMonthKey] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snapping, setSnapping] = useState(false);
  const [snappingUserId, setSnappingUserId] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, type: null, userId: null, userName: '' });

  // Sửa tay số liệu tháng — xem backend/models/CommissionAdjustment.js
  const [adjustments, setAdjustments] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [editForm, setEditForm] = useState({ targetGMV: '', commissionOverride: '', reason: '' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [revertTarget, setRevertTarget] = useState(null);
  const [historyRow, setHistoryRow] = useState(null);   // điều chỉnh đang mở lịch sử
  const [rollingBack, setRollingBack] = useState(null); // _id dòng lịch sử đang khôi phục

  // Tra nhanh điều chỉnh đang áp của từng người
  const adjByUser = React.useMemo(() => {
    const m = {};
    for (const a of adjustments) m[String(a.userId?._id || a.userId)] = a;
    return m;
  }, [adjustments]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [result, adjRes] = await Promise.all([
        adminGetAllCommissions(monthKey),
        adminGetCommissionAdjustments(monthKey),
      ]);
      if (result.success) {
        setData(result.data);
      } else {
        showNotification(result.error?.message || 'Không thể tải dữ liệu', 'error');
      }
      setAdjustments(adjRes.success ? (adjRes.data || []) : []);
    } catch (err) {
      showNotification('Lỗi kết nối server', 'error');
    }
    setLoading(false);
  }, [monthKey]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const showNotification = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const handleSnapshotUser = async (userId, userName) => {
    setSnappingUserId(userId);
    try {
      const result = await adminSnapshotUser(userId, monthKey);
      if (result.success) {
        showNotification(result.message || `Đã chốt hoa hồng cho ${userName}`);
        fetchData();
      } else {
        showNotification(result.error?.message || 'Lỗi chốt hoa hồng', 'error');
      }
    } catch (err) {
      showNotification('Lỗi server', 'error');
    }
    setSnappingUserId(null);
    setConfirmDialog({ open: false, type: null, userId: null, userName: '' });
  };

  const handleSnapshotAll = async () => {
    setSnapping(true);
    try {
      const result = await adminSnapshotAll(monthKey);
      if (result.success) {
        showNotification(result.message || 'Đã chốt hoa hồng cho tất cả');
        fetchData();
      } else {
        showNotification(result.error?.message || 'Lỗi chốt hoa hồng', 'error');
      }
    } catch (err) {
      showNotification('Lỗi server', 'error');
    }
    setSnapping(false);
    setConfirmDialog({ open: false, type: null, userId: null, userName: '' });
  };

  const openEdit = (u) => {
    const a = adjByUser[String(u.userId)];
    setEditRow(u);
    setEditForm({
      targetGMV: u.totalGMV || '',
      commissionOverride: a?.commissionOverride ?? '',
      reason: '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editForm.reason.trim()) return showNotification('Nhập lý do sửa', 'error');
    const gmv = editForm.targetGMV === '' ? null : Number(editForm.targetGMV);
    const com = editForm.commissionOverride === '' ? null : Number(editForm.commissionOverride);
    if (gmv === null && com === null) {
      return showNotification('Nhập ít nhất một trong hai: Tổng doanh thu hoặc Hoa hồng', 'error');
    }
    setSavingEdit(true);
    const res = await adminAdjustCommission({
      userId: editRow.userId, monthKey, targetGMV: gmv, commissionOverride: com,
      reason: editForm.reason.trim(),
    });
    setSavingEdit(false);
    if (res.success) {
      showNotification(res.message || 'Đã cập nhật');
      (res.warnings || []).forEach(w => showNotification(w, 'warning'));
      setEditRow(null);
      fetchData();
    } else {
      showNotification(res.error?.message || 'Lỗi lưu', 'error');
    }
  };

  const handleRevert = async () => {
    const res = await adminRevertCommissionAdjustment(revertTarget._id);
    if (res.success) {
      showNotification(res.message);
      (res.warnings || []).forEach(w => showNotification(w, 'warning'));
      fetchData();
    } else showNotification(res.error?.message || 'Lỗi hoàn nguyên', 'error');
    setRevertTarget(null);
  };

  const handleRollback = async (entry) => {
    setRollingBack(entry._id);
    const res = await adminRollbackCommissionAdjustment(historyRow._id, entry._id);
    setRollingBack(null);
    if (res.success) {
      showNotification(res.message);
      (res.warnings || []).forEach(w => showNotification(w, 'warning'));
      setHistoryRow(null);
      fetchData();
    } else showNotification(res.error?.message || 'Lỗi khôi phục', 'error');
  };

  const usersWithGMV = data?.users?.filter(u => u.totalGMV > 0 || u.commissionPercent > 0) || [];

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <MonetizationOnIcon sx={{ fontSize: 32, color: '#d97706' }} />
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={700}>Quản lý Hoa hồng</Typography>
          <Typography variant="body2" color="text.secondary">Tổng hợp hoa hồng theo tháng</Typography>
        </Box>
      </Box>

      {/* Controls */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
          <TextField
            type="month"
            label="Chọn tháng"
            value={monthKey}
            onChange={(e) => setMonthKey(e.target.value)}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchData}
            disabled={loading}
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Làm mới
          </Button>
          <Button
            variant="contained"
            startIcon={snapping ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
            onClick={() => setConfirmDialog({ open: true, type: 'all', userId: null, userName: '' })}
            disabled={snapping || loading}
            sx={{
              borderRadius: 2, textTransform: 'none',
              background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)',
              '&:hover': { background: 'linear-gradient(135deg, #b45309 0%, #92400e 100%)' }
            }}
          >
            Chốt tất cả tháng {monthKey}
          </Button>
        </Stack>
      </Paper>

      {/* Summary Cards */}
      {data?.summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={4}>
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #bfdbfe', bgcolor: '#eff6ff' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <TrendingUpIcon sx={{ fontSize: 28, color: '#2563eb', mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: '#3b82f6', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>
                  Tổng doanh thu
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#1e40af' }}>
                  {formatMoney(data.summary.totalGMV)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #a7f3d0', bgcolor: '#ecfdf5' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <MonetizationOnIcon sx={{ fontSize: 28, color: '#059669', mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: '#059669', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>
                  Tổng hoa hồng
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#065f46' }}>
                  {formatMoney(data.summary.totalCommission)}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
              <CardContent sx={{ textAlign: 'center', py: 2 }}>
                <PeopleIcon sx={{ fontSize: 28, color: '#64748b', mb: 0.5 }} />
                <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600, textTransform: 'uppercase', display: 'block' }}>
                  Nhân viên
                </Typography>
                <Typography variant="h5" fontWeight={700} sx={{ color: '#334155' }}>
                  {data.summary.userCount}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : usersWithGMV.length > 0 ? (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow sx={{ bgcolor: '#f8fafc' }}>
                <TableCell sx={{ fontWeight: 700 }}>Nhân viên</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Doanh thu</TableCell>
                {!isMobile && <TableCell align="center" sx={{ fontWeight: 700 }}>% Hoa hồng</TableCell>}
                <TableCell align="right" sx={{ fontWeight: 700 }}>Hoa hồng</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Thưởng Lead</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Trạng thái</TableCell>
                <TableCell align="center" sx={{ fontWeight: 700 }}>Thao tác</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {usersWithGMV.map((u) => (
                <TableRow key={u.userId} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{u.fullName}</Typography>
                    <Typography variant="caption" color="text.secondary">@{u.username}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#1e40af' }}>
                      {formatMoney(u.totalGMV)}
                    </Typography>
                    {adjByUser[String(u.userId)]?.targetGMV != null && (
                      <Tooltip title={`Đã sửa tay từ ${formatMoney(adjByUser[String(u.userId)].originalGMV)} — ${adjByUser[String(u.userId)].reason}`}>
                        <Chip size="small" label="đã sửa" color="warning" variant="outlined"
                          sx={{ height: 18, fontSize: '0.6rem', mt: 0.3 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  {!isMobile && (
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                        <Chip
                          label={`${u.commissionPercent}%`}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            bgcolor: u.commissionPercent > 0 ? '#fef3c7' : '#f1f5f9',
                            color: u.commissionPercent > 0 ? '#92400e' : '#94a3b8'
                          }}
                        />
                        {(u.isTimeBased || u.hasConfig) && (
                          <Chip
                            icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                            label="Theo giờ"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.6rem',
                              bgcolor: '#dbeafe',
                              color: '#1e40af',
                              '& .MuiChip-icon': { color: '#1e40af' },
                              '& .MuiChip-label': { px: 0.5 }
                            }}
                          />
                        )}
                      </Box>
                    </TableCell>
                  )}
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight={700} sx={{ color: '#065f46' }}>
                      {formatMoney(u.commissionAmount)}
                    </Typography>
                    {adjByUser[String(u.userId)]?.commissionOverride != null && (
                      <Tooltip title={`Ghi đè tay, số hệ thống tính là ${formatMoney(adjByUser[String(u.userId)].originalCommission)} — ${adjByUser[String(u.userId)].reason}`}>
                        <Chip size="small" label="ghi đè" color="warning" variant="outlined"
                          sx={{ height: 18, fontSize: '0.6rem', mt: 0.3 }} />
                      </Tooltip>
                    )}
                  </TableCell>
                  {/* Thưởng Lead tính trên GMV TOÀN TEAM — sửa GMV của một liver thì cột này đổi
                      theo, nên phải hiện ra để admin thấy được hệ quả. */}
                  <TableCell align="right">
                    {u.leaderBonus > 0 ? (
                      <Tooltip title="Hoa hồng team 0,4% + thưởng tăng trưởng 2% + thưởng TikTok — tính trên GMV toàn team">
                        <Typography variant="body2" fontWeight={700} sx={{ color: '#7c3aed' }}>
                          {formatMoney(u.leaderBonus)}
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography variant="caption" color="text.secondary">—</Typography>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    {u.isSnapshot ? (
                      <Chip icon={<CheckCircleIcon />} label="Đã chốt" size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label="Tạm tính" size="small" variant="outlined" sx={{ color: '#d97706', borderColor: '#fde68a' }} />
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Sửa tổng doanh thu / hoa hồng tháng này">
                      <IconButton size="small" onClick={() => openEdit(u)} sx={{ color: '#1e40af' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {adjByUser[String(u.userId)] && (
                      <Tooltip title={`Lịch sử sửa (${(adjByUser[String(u.userId)].history || []).length} lần)`}>
                        <IconButton size="small" onClick={() => setHistoryRow(adjByUser[String(u.userId)])} sx={{ color: '#7c3aed' }}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {adjByUser[String(u.userId)]?.status === 'active' && (
                      <Tooltip title="Hoàn nguyên về số gốc">
                        <IconButton size="small" onClick={() => setRevertTarget(adjByUser[String(u.userId)])} sx={{ color: '#16a34a' }}>
                          <UndoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {!u.isSnapshot && (
                      <Tooltip title="Chốt hoa hồng">
                        <IconButton
                          size="small"
                          onClick={() => setConfirmDialog({ open: true, type: 'user', userId: u.userId, userName: u.fullName })}
                          disabled={snappingUserId === u.userId}
                          sx={{ color: '#d97706' }}
                        >
                          {snappingUserId === u.userId ? <CircularProgress size={18} /> : <LockIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {u.isSnapshot && (
                      <Tooltip title="Chốt lại">
                        <IconButton
                          size="small"
                          onClick={() => setConfirmDialog({ open: true, type: 'user', userId: u.userId, userName: u.fullName })}
                          disabled={snappingUserId === u.userId}
                          sx={{ color: '#94a3b8' }}
                        >
                          {snappingUserId === u.userId ? <CircularProgress size={18} /> : <RefreshIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Không có dữ liệu hoa hồng cho tháng {monthKey}
        </Alert>
      )}

      {/* Confirm Dialog */}
      <Dialog open={confirmDialog.open} onClose={() => setConfirmDialog({ open: false, type: null, userId: null, userName: '' })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Xác nhận chốt hoa hồng
        </DialogTitle>
        <DialogContent>
          <Typography>
            {confirmDialog.type === 'all'
              ? `Chốt hoa hồng tháng ${monthKey} cho TẤT CẢ nhân viên? Dữ liệu sẽ được lưu snapshot và không bị ảnh hưởng khi thay đổi % sau này.`
              : `Chốt hoa hồng tháng ${monthKey} cho ${confirmDialog.userName}?`
            }
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setConfirmDialog({ open: false, type: null, userId: null, userName: '' })}
            variant="outlined"
            sx={{ borderRadius: 2, textTransform: 'none' }}
          >
            Hủy
          </Button>
          <Button
            onClick={() => confirmDialog.type === 'all' ? handleSnapshotAll() : handleSnapshotUser(confirmDialog.userId, confirmDialog.userName)}
            variant="contained"
            disabled={snapping || snappingUserId}
            startIcon={(snapping || snappingUserId) ? <CircularProgress size={16} color="inherit" /> : <LockIcon />}
            sx={{
              borderRadius: 2, textTransform: 'none',
              background: 'linear-gradient(135deg, #d97706 0%, #b45309 100%)'
            }}
          >
            Xác nhận chốt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification */}
      {/* Sửa số liệu tháng */}
      <Dialog open={!!editRow} onClose={() => setEditRow(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Sửa số liệu tháng {monthKey} — {editRow?.fullName}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              fullWidth size="small" type="number" label="Tổng doanh thu (GMV) của tháng"
              value={editForm.targetGMV}
              onChange={(e) => setEditForm(f => ({ ...f, targetGMV: e.target.value }))}
              helperText={editForm.targetGMV !== '' && editRow
                ? `Hiện ${formatMoney(editRow.totalGMV)} → ${formatMoney(Number(editForm.targetGMV))}`
                : 'Bỏ trống nếu không đổi GMV'}
            />
            <Alert severity="info" variant="outlined" sx={{ borderRadius: 2 }}>
              Sửa GMV thì các phiếu doanh thu trong tháng <b>co giãn cùng tỷ lệ</b> (giữ đúng khung giờ
              nên hoa hồng theo khung vẫn chuẩn). Kéo theo tính lại: hoa hồng cá nhân, mốc thưởng GMV,
              <b> hoa hồng team 0,4%</b>, <b>thưởng tăng trưởng 2%</b> của Lead, và phiếu lương kỳ đó.
            </Alert>

            <TextField
              fullWidth size="small" type="number" label="Ghi đè số tiền hoa hồng (tuỳ chọn)"
              value={editForm.commissionOverride}
              onChange={(e) => setEditForm(f => ({ ...f, commissionOverride: e.target.value }))}
              helperText={editForm.commissionOverride !== '' && editRow
                ? `Hệ thống tính ${formatMoney(editRow.commissionAmount)} → ghi đè thành ${formatMoney(Number(editForm.commissionOverride))}`
                : 'Bỏ trống để dùng số hệ thống tự tính'}
            />
            {editForm.commissionOverride !== '' && (
              <Alert severity="warning" variant="outlined" sx={{ borderRadius: 2 }}>
                Ghi đè hoa hồng <b>không</b> làm đổi hoa hồng team và thưởng tăng trưởng của Lead —
                hai khoản đó là % của <b>GMV</b>, không phải % của hoa hồng. Muốn chúng đổi thì sửa GMV.
              </Alert>
            )}

            <TextField
              fullWidth size="small" multiline rows={2} label="Lý do sửa (bắt buộc)"
              value={editForm.reason}
              onChange={(e) => setEditForm(f => ({ ...f, reason: e.target.value }))}
              placeholder="VD: đối chiếu Shopee Seller Center ngày 01/09, số thật của tháng là 700tr."
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setEditRow(null)} sx={{ textTransform: 'none', borderRadius: 2 }}>Huỷ</Button>
          <Button variant="contained" onClick={handleSaveEdit} disabled={savingEdit} sx={{ textTransform: 'none', borderRadius: 2 }}>
            {savingEdit ? <CircularProgress size={18} color="inherit" /> : 'Lưu & tính lại'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lịch sử sửa + khôi phục về từng mốc */}
      <Dialog open={!!historyRow} onClose={() => setHistoryRow(null)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Lịch sử sửa — {historyRow?.userId?.fullName} · tháng {historyRow?.monthKey}
        </DialogTitle>
        <DialogContent dividers>
          <Alert severity="info" variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
            Số gốc ban đầu: <b>{formatMoney(historyRow?.baselineGMV)}</b> doanh thu ·{' '}
            <b>{formatMoney(historyRow?.baselineCommission)}</b> hoa hồng. Mọi lần sửa đều tính lại
            từ số gốc này, nên sửa bao nhiêu lần cũng không lệch dần.
          </Alert>
          <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid #e2e8f0' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#f8fafc' }}>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Thời điểm</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Người sửa</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Thao tác</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Doanh thu sau</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700 }}>Hoa hồng sau</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Lý do</TableCell>
                  <TableCell align="center" sx={{ fontWeight: 700 }}>Khôi phục</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(historyRow?.history || []).map((h, i) => {
                  const isLast = i === (historyRow.history.length - 1);
                  const label = h.action === 'revert' ? 'Về số gốc'
                    : h.action === 'rollback' ? `Khôi phục mốc #${h.rolledBackTo}` : 'Sửa';
                  return (
                    <TableRow key={h._id} hover sx={{ bgcolor: isLast ? '#f0fdf4' : undefined }}>
                      <TableCell>{i + 1}</TableCell>
                      <TableCell>{new Date(h.at).toLocaleString('vi-VN')}</TableCell>
                      <TableCell>{h.byName || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" variant="outlined" label={label}
                          color={h.action === 'revert' ? 'success' : h.action === 'rollback' ? 'info' : 'warning'} />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(h.resultGMV)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(h.resultCommission)}</TableCell>
                      <TableCell sx={{ maxWidth: 220 }}>
                        <Typography variant="caption">{h.reason || '—'}</Typography>
                      </TableCell>
                      <TableCell align="center">
                        {isLast ? (
                          <Chip size="small" label="đang áp" color="success" sx={{ height: 20, fontSize: '0.65rem' }} />
                        ) : (
                          <Tooltip title={`Đưa số liệu tháng về đúng mốc này (${formatMoney(h.resultGMV)})`}>
                            <span>
                              <IconButton size="small" color="primary" disabled={!!rollingBack}
                                onClick={() => handleRollback(h)}>
                                {rollingBack === h._id ? <CircularProgress size={16} /> : <RestoreIcon fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
            Khôi phục cũng được ghi thành một dòng mới, nên quay tới quay lui bao nhiêu lần vẫn lần ngược lại được.
            Hoa hồng team 0,4% và thưởng tăng trưởng 2% của Lead tính lại theo mỗi lần khôi phục.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button variant="outlined" onClick={() => setHistoryRow(null)} sx={{ textTransform: 'none', borderRadius: 2 }}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Hoàn nguyên */}
      <Dialog open={!!revertTarget} onClose={() => setRevertTarget(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Hoàn nguyên số liệu?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Trả doanh thu tháng {revertTarget?.monthKey} về {formatMoney(revertTarget?.originalGMV)}, bỏ ghi đè
            hoa hồng, và tính lại phiếu lương kỳ đó. Hoa hồng team và thưởng tăng trưởng của Lead cũng về theo.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button variant="outlined" onClick={() => setRevertTarget(null)} sx={{ textTransform: 'none', borderRadius: 2 }}>Huỷ</Button>
          <Button variant="contained" color="success" onClick={handleRevert} sx={{ textTransform: 'none', borderRadius: 2 }}>Hoàn nguyên</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert severity={notification.severity} variant="filled" sx={{ borderRadius: 2 }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommissionManagement;
